import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import { getSpecificUsers } from "../../../api/users";
import { getOrder } from "api/orders";
import { setCurrentDevices } from "redux/reducers/devicesReducer";
import { setCurrentOrder } from "redux/reducers/ordersReducer";
import { getDevice } from "api/devices";
import { Link } from "react-router-dom";

import MDBox from "../../../components/MDBox";
import MDModal from "../../../components/MDModal";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";

import { putStage, postStage, getStage, deleteStage } from "../../../api/stages";
import { getSimplifiedOrders } from "../../../api/orders";
import { getSimplifiedDevices } from "../../../api/devices";

import Stage from "./Stage";

import PropTypes from "prop-types";

export default function CreateEditStages({
  edit = false,
  stages,
  setStages,
  device = null,
  global = false,
  user = null,
  userId = null,
  currentOrderId = null,
  editOpen = false,
  setEditOpen = null,
  renderStep = true,
}) {
  const [open, setOpen] = useState(false);

  const [step, setStep] = useState(0);
  const steps = edit && !global ? stages.map((_, i) => `phase ${i + 1}`) : ["phase 1"];
  const isLastStep = !global ? step === steps.length - 1 : true;
  const isFirstStep = !global ? step === 0 : true;

  const [stageId, setStageId] = useState("");
  const [stageDescription, setStageDescription] = useState("");
  const [stageStartDate, setStageStartDate] = useState(null);
  const [stageEndDate, setStageEndDate] = useState(null);
  const [stageDuration, setStageDuration] = useState(0);
  const [ressource1UserId, setRessource1UserId] = useState(null);
  const [ressource2UserId, setRessource2UserId] = useState(null);
  const [ressource3UserId, setRessource3UserId] = useState(null);
  const [ressource4UserId, setRessource4UserId] = useState(null);
  const [mo, setMo] = useState(null);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");

  const currentOrder = useSelector((state) => state.orders.currentOrder);
  const dispatch = useDispatch();

  async function fetchMo() {
    const response = await getSpecificUsers("mo");
    setMo(response.data);
  }

  useEffect(() => {
    fetchMo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (global === true) {
      setStep(false);
      if (!edit) {
        fetchAvailableOrders();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [global]);

  useEffect(() => {
    if (selectedOrderId && selectedOrderId.id) {
      fetchAvailableDevices(selectedOrderId.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderId]);

  useEffect(() => {
    if ((edit && stages && stages[step] !== undefined) || (edit && global && stages)) {
      const selectedStage = global ? stages : stages[step];
      setStageId(selectedStage.stage_id);
      setStageDescription(
        selectedStage.stage_description === "montage prévisionnel"
          ? ""
          : selectedStage.stage_description
      );
      setStageStartDate(selectedStage.stage_start_date);
      setStageEndDate(selectedStage.stage_end_date);
      setStageDuration(selectedStage.stage_duration);
      setRessource1UserId(
        selectedStage.ressource1_id !== null
          ? {
              label: selectedStage.ressource1_name,
              id: selectedStage.ressource1_id,
            }
          : null
      );
      setRessource2UserId(
        selectedStage.ressource2_id !== null
          ? {
              label: selectedStage.ressource2_name,
              id: selectedStage.ressource2_id,
            }
          : null
      );
      setRessource3UserId(
        selectedStage.ressource3_id !== null
          ? {
              label: selectedStage.ressource3_name,
              id: selectedStage.ressource3_id,
            }
          : null
      );
      setRessource4UserId(
        selectedStage.ressource4_id !== null
          ? {
              label: selectedStage.ressource4_name,
              id: selectedStage.ressource4_id,
            }
          : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, stages]);

  async function createStageAPI() {
    const stage = {
      device_id: !global ? device.device_id : selectedDeviceId.id,
      stage_description: stageDescription,
      stage_start_date: stageStartDate ? moment(stageStartDate).format("YYYY-MM-DD") : null,
      stage_end_date: stageEndDate ? moment(stageEndDate).format("YYYY-MM-DD") : null,
      stage_duration: stageDuration,
      ressource1: ressource1UserId ? ressource1UserId.id : null,
      ressource2: ressource2UserId ? ressource2UserId.id : null,
      ressource3: ressource3UserId ? ressource3UserId.id : null,
      ressource4: ressource4UserId ? ressource4UserId.id : null,
    };
    await postStage(stage);
  }

  async function deleteStageAPI() {
    await deleteStage(stageId);
  }

  async function updateStageAPI() {
    const stage = {
      stage_description: stageDescription,
      stage_start_date: stageStartDate ? moment(stageStartDate).format("YYYY-MM-DD") : null,
      stage_end_date: stageEndDate ? moment(stageEndDate).format("YYYY-MM-DD") : null,
      stage_duration: stageDuration,
      ressource1: ressource1UserId ? ressource1UserId.id : null,
      ressource2: ressource2UserId ? ressource2UserId.id : null,
      ressource3: ressource3UserId ? ressource3UserId.id : null,
      ressource4: ressource4UserId ? ressource4UserId.id : null,
    };
    await putStage(stage, stageId);
  }

  async function fetchAvailableOrders() {
    const response = await getSimplifiedOrders(user.user_id, user.role);
    setAvailableOrders(response.data);
  }

  async function fetchAvailableDevices(orderId) {
    const response = await getSimplifiedDevices(orderId);
    setAvailableDevices(response.data);
  }

  async function fetchOrderDeleted(deviceId) {
    const response = await getOrder(deviceId);
    return response.data.order_id;
  }

  async function fetchOrderId(deviceId) {
    const response = await getDevice(deviceId);
    return response.data.order_id;
  }

  async function fetchStages() {
    if (global && !userId) {
      const response = await getStage("nothing", false, true);
      setStages(response.data);
    } else if (global && userId) {
      const response = await getStage(userId, true);
      const stagesWithAdditionalInfo = await Promise.all(
        response.data.map(async (stage) => {
          const deviceId = await fetchOrderId(stage.device_id);
          const orderShadowDeleted = await fetchOrderDeleted(deviceId);
          return {
            ...stage,
            order_id: orderShadowDeleted,
          };
        })
      );
      const filteredStages = stagesWithAdditionalInfo.filter(
        (stage) => stage.order_id !== undefined
      );
      setStages(filteredStages);
    } else if (device) {
      const response = await getStage(device.device_id);
      setStages(response.data);
    }
  }

  async function fetchOrder() {
    const response = await getOrder(currentOrder.order_id);
    dispatch(setCurrentOrder(response.data));
  }

  async function fetchDevice(deviceId) {
    const response = await getDevice(deviceId);
    return await response.data;
  }

  const fetchDevices = async () => {
    if (currentOrder) {
      if (currentOrder.devices_ids) {
        const devices = [];
        const devices_id = currentOrder.devices_ids.split(",");
        for (let i = 0; i < devices_id.length; i++) {
          devices.push(await fetchDevice(devices_id[i]));
        }

        // Sort the devices array based on the device identifier
        devices.sort((a, b) => {
          // Extract the base device identifier (ignoring anything after a hyphen)
          const baseIdA = a.device_number.split(" -")[0];
          const baseIdB = b.device_number.split(" -")[0];

          // Compare the base identifiers
          if (baseIdA < baseIdB) return -1;
          if (baseIdA > baseIdB) return 1;

          // If base identifiers are equal, compare the full device_id to maintain relative order
          return a.device_number.localeCompare(b.device_number);
        });

        dispatch(setCurrentDevices(devices));
      }
    }
  };

  useEffect(() => {
    if (!global) {
      fetchStages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetData() {
    if (global) {
      setSelectedOrderId("");
      setSelectedDeviceId("");
    }
    setStageDescription("");
    setStageStartDate(null);
    setStageEndDate(null);
    setStageDuration(0);
    setRessource1UserId(null);
    setRessource2UserId(null);
    setRessource3UserId(null);
    setRessource4UserId(null);
  }

  async function deleteSt() {
    await deleteStageAPI();
    await fetchStages();
    exit();
  }

  async function submit() {
    if (edit) {
      await updateStageAPI();
      await fetchStages();
    } else {
      await createStageAPI();
      await fetchStages();
      if (!global) {
        await fetchOrder();
        await fetchDevices();
      }
      resetData();
    }
    exit();
  }

  async function cancel() {
    if (edit) {
      await fetchStages();
    } else {
      resetData();
    }
    exit();
  }

  function exit() {
    setStep(0);
    if (setEditOpen !== null) {
      setEditOpen(false);
    }
    setOpen(false);
  }

  const next = () => {
    setStep(step + 1);
  };

  const previous = () => setStep(step - 1);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <>
      {!global && edit ? (
        <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
          <Icon style={{ color: darkMode ? "#344767" : "#FFF" }}>edit</Icon>
        </IconButton>
      ) : (
        !edit && (
          <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
            <Icon style={{ color: darkMode ? "#344767" : "#FFF" }}>add</Icon>
          </IconButton>
        )
      )}
      <MDModal
        open={(open || editOpen) && mo !== null}
        onClose={() => {
          if (setEditOpen !== null) setEditOpen(false);
          setOpen(false);
        }}
      >
        <MDBox mt={20} mb={9}>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox
                  mt={edit && !global ? -12 : -5}
                  mb={2}
                  mx={2}
                  variant="gradient"
                  sx={{
                    background: darkMode ? "white" : "linear-gradient(195deg, #364c70, #273956)",
                    boxShadow: darkMode ? "0 1px 6px 1px white" : "0 1px 6px 1px #344767",
                  }}
                  borderRadius="lg"
                  textAlign="center"
                >
                  <MDBox mt={2} mb={1}>
                    <MDBox>
                      {edit ? (
                        <>
                          <MDTypography
                            variant="h3"
                            fontWeight="bold"
                            color={darkMode ? "inherit" : "white"}
                          >
                            Modification des phases du chantier
                          </MDTypography>
                          <MDTypography
                            variant="h5"
                            fontWeight="regular"
                            color={darkMode ? "inherit" : "white"}
                          >
                            Veuillez rentrer les informations relatives aux phases du chantier
                          </MDTypography>
                        </>
                      ) : (
                        <>
                          <MDTypography
                            variant="h3"
                            fontWeight="bold"
                            color={darkMode ? "inherit" : "white"}
                          >
                            Ajouter une nouvelle phase
                          </MDTypography>
                          <MDTypography
                            variant="h5"
                            fontWeight="regular"
                            color={darkMode ? "inherit" : "white"}
                          >
                            Veuillez rentrer les informations relatives aux phases du chantier
                          </MDTypography>
                        </>
                      )}
                    </MDBox>
                  </MDBox>
                  {edit && renderStep && (
                    <Stepper
                      sx={{ boxShadow: "none", backgroundImage: "none" }}
                      activeStep={step}
                      alternativeLabel
                    >
                      {steps.map((label) => (
                        <Step
                          sx={{
                            "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-active":
                              {
                                color: darkMode && "#344767 !important",
                              },
                            "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-completed":
                              {
                                color: darkMode && "#344767 !important",
                              },
                          }}
                          key={label}
                        >
                          <StepLabel
                            sx={{
                              "& .MuiStepLabel-label.Mui-active": {
                                color: darkMode && "#344767 !important",
                              },
                              "& .MuiStepLabel-label.Mui-completed": {
                                color: darkMode && "#344767 !important",
                              },
                              "& .MuiStepIcon-root.Mui-active": {
                                background: darkMode && "#344767",
                                fill: darkMode && "#344767",
                                stroke: darkMode && "#344767",
                                borderColor: darkMode && "#344767",
                                boxShadow: darkMode && "0rem 0rem 0rem 0.125rem #344767",
                              },
                              "& .MuiStepIcon-root.Mui-completed": {
                                background: darkMode && "#344767",
                                fill: darkMode && "#344767",
                                stroke: darkMode && "#344767",
                                borderColor: darkMode && "#344767",
                                boxShadow: darkMode && "0rem 0rem 0rem 0.125rem #344767",
                              },
                            }}
                          >
                            {label}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  )}
                </MDBox>
                <MDBox p={2}>
                  <MDBox>
                    <Stage
                      mo={mo}
                      stageDescription={stageDescription}
                      setStageDescription={setStageDescription}
                      stageStartDate={stageStartDate}
                      setStageStartDate={setStageStartDate}
                      stageEndDate={stageEndDate}
                      setStageEndDate={setStageEndDate}
                      stageDuration={stageDuration}
                      setStageDuration={setStageDuration}
                      ressource1UserId={ressource1UserId}
                      setRessource1UserId={setRessource1UserId}
                      ressource2UserId={ressource2UserId}
                      setRessource2UserId={setRessource2UserId}
                      ressource3UserId={ressource3UserId}
                      setRessource3UserId={setRessource3UserId}
                      ressource4UserId={ressource4UserId}
                      setRessource4UserId={setRessource4UserId}
                      availableOrders={availableOrders}
                      selectedOrderId={selectedOrderId}
                      setSelectedOrderId={setSelectedOrderId}
                      availableDevices={availableDevices}
                      setAvailableDevices={setAvailableDevices}
                      selectedDeviceId={selectedDeviceId}
                      setSelectedDeviceId={setSelectedDeviceId}
                      global={global}
                      edit={edit}
                    />
                    <MDBox mt={3} width="100%" display="flex" justifyContent="space-between">
                      <MDBox width="25%">
                        {global && currentOrderId && (
                          <MDButton
                            variant="gradient"
                            component={Link}
                            to={
                              currentOrderId &&
                              window &&
                              `/commandes/${encodeURIComponent(currentOrderId)}`
                            }
                            color="info"
                          >
                            Accéder à la commande
                          </MDButton>
                        )}
                        {isFirstStep ? (
                          <MDBox />
                        ) : (
                          <MDButton variant="gradient" color="warning" onClick={previous}>
                            Précédent
                          </MDButton>
                        )}
                      </MDBox>
                      <MDBox textAlign="right" width="75%">
                        <Grid container justifyContent="right" spacing={1.5}>
                          {global || (isLastStep && steps.length > 0) ? (
                            <MDBox />
                          ) : (
                            <Grid item>
                              <MDButton variant="gradient" color="info" onClick={next}>
                                Suivant
                              </MDButton>
                            </Grid>
                          )}
                          <Grid item>
                            <MDButton variant="gradient" color="warning" onClick={cancel}>
                              Annuler
                            </MDButton>
                          </Grid>
                          <Grid item>
                            <MDButton
                              disabled={
                                (((step && !edit) || (global && !selectedDeviceId)) &&
                                  !stageStartDate &&
                                  !stageEndDate) ||
                                stageDescription === ""
                              }
                              variant="gradient"
                              color="success"
                              onClick={submit}
                            >
                              Envoyer
                            </MDButton>
                          </Grid>
                          {edit && (
                            <Grid item>
                              <MDButton variant="gradient" color="error" onClick={deleteSt}>
                                Supprimer la phase
                              </MDButton>
                            </Grid>
                          )}
                        </Grid>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDModal>
    </>
  );
}

CreateEditStages.propTypes = {
  edit: PropTypes.bool.isRequired,
};
